import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import './CajaPanel.scss';
import PendingCash from '../../../../../Caja/CajasManagement/components/PendingCash/PendingCash';
import IndividualCaja from '../../../../../Caja/CajasManagement/components/IndividualCaja/IndividualCaja';
import Recibos from './components/Recibos/Recibos';
import Movimientos from './components/Movimientos/Movimientos';
import { getLocalCajaManagementByLocalId } from '../../../../../../../crud/management_api/locales_caja_management';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../../../../../../components/common/LoadingSpinner';

type SubTab = 'listado' | 'recibos' | 'movimientos';

interface Caja {
  workstation_id: number;
  workstation_name: string;
  last_modified: string;
  last_opened: string;
  last_closed: string;
  user_id: number;
  user_name: string;
  is_open: boolean;
  initial_amount: number;
  current_amount: number;
  final_amount: number;
}

interface CajaPanelProps {
  isActive: boolean; // Whether this panel is currently visible
}

const CajaPanel: React.FC<CajaPanelProps> = ({ isActive }) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('listado');
  const [cajas, setCajas] = useState<Caja[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch cajas data only when this panel becomes active
  useEffect(() => {
    if (isActive && !dataLoaded) {
      fetchCajasData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, dataLoaded]);

  // Refetch when switching back to listado sub-tab (to get latest data)
  useEffect(() => {
    if (isActive && activeSubTab === 'listado' && dataLoaded) {
      fetchCajasData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, activeSubTab, dataLoaded]);

  const fetchCajasData = () => {
    setLoading(true);
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = Number(user.local_id) || 0;

    return getLocalCajaManagementByLocalId(localId)
      .then((response) => {
        setCajas(response.data);
        setDataLoaded(true);
        return response.data;
      })
      .catch((error) => {
        console.error('Error getting cajas: ', error);
        toast.error('Error obteniendo cajas');
        throw error;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const refreshCajas = () => {
    return fetchCajasData();
  };

  if (!isActive) {
    return null;
  }

  if (loading && !dataLoaded) {
    return (
      <div className="caja-panel">
        <LoadingSpinner text="Cargando información de cajas..." transparent />
      </div>
    );
  }

  const openCajasCount = cajas.filter(c => c.is_open).length;

  return (
    <div className="caja-panel">
      {/* Sub-tabs navigation */}
      <div className="caja-panel__subtabs">
        <Button
          variant="none"
          className={`caja-panel__subtab ${activeSubTab === 'listado' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('listado')}
        >
          Listado
        </Button>
        <Button
          variant="none"
          className={`caja-panel__subtab ${activeSubTab === 'recibos' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('recibos')}
        >
          Recibos
        </Button>
        <Button
          variant="none"
          className={`caja-panel__subtab ${activeSubTab === 'movimientos' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('movimientos')}
        >
          Movimientos
        </Button>
      </div>

      {/* Content based on active sub-tab */}
      <div className="caja-panel__content">
        {activeSubTab === 'listado' && (
          <div className="caja-panel__listado">
            {/* Pending Cash Transactions */}
            <PendingCash
              key={`pending-${cajas.map(c => c.workstation_id).join('-')}-${cajas.map(c => c.current_amount).join('-')}`}
              cajas={cajas}
              setCajas={setCajas}
              refreshCajas={refreshCajas}
            />

            {/* List of Individual Cajas */}
            <div className="caja-panel__cajas-list">
              {cajas.map((caja) => (
                <IndividualCaja
                  key={`caja-${caja.workstation_id}-${caja.current_amount}-${caja.last_modified}`}
                  caja={caja}
                  cajas={cajas}
                  setCajas={setCajas}
                  isSessionActive={true} // TODO: Get from session context
                  hasPendientes={false} // TODO: Get pending data
                  openCajasCount={openCajasCount}
                />
              ))}
            </div>
          </div>
        )}

        {activeSubTab === 'recibos' && (
          <Recibos isActive={activeSubTab === 'recibos'} />
        )}

        {activeSubTab === 'movimientos' && (
          <Movimientos isActive={activeSubTab === 'movimientos'} />
        )}
      </div>
    </div>
  );
};

export default CajaPanel;
